<script setup lang="ts">
import { CalendarList } from '@/class/pages/CalendarList'
import { GroupList } from '@/class/pages/GroupList'
import AlertPopup from '@/components/common/AlertPopup.vue'
import CalendarLinkModal from '@/components/Settings/CalendarLinkModal.vue'
import CalendarListTable from '@/components/Settings/CalendarListTable.vue'
import CreateCalendarModalWithSchedule from '@/components/Settings/CreateCalendarModalWithSchedule.vue'
import CreateGroupModal from '@/components/Settings/CreateGroupModal.vue'
import SelectCalendars from '@/components/Settings/Groups/SelectCalendars.vue'
import SelectCalendarTypeModalVue_V2 from '@/components/Settings/SelectCalendarTypeModalV2.vue'
import SelectTeamModalVue from '@/components/Settings/SelectTeamModal.vue'
import Calendar, { CalendarType } from '@/models/calendar'
import {
  getCalendarOnboardingStatus,
  getCalendarsInLocation,
  getEquipmentsInLocation,
  getRoomsInLocation,
  getTeamsInLocation,
  getUsersInLocation,
  isEquipmentEnabled,
  isRentalsEnabled,
  isRoomEnabled,
  isServiceCalendarEnabled,
  resetCalendarEditValidationData,
  setEditingCalendar,
  setIsLoading,
  updateCalendarStore,
  updateTeamsStore,
} from '@/states/app'
import { UISpin, UITabPane, UITabs } from '@gohighlevel/ghl-ui'
import { computed, onMounted, ref, watch } from 'vue'
import { useRoute } from 'vue-router'
import CalendarGroupList from '../components/Settings/CalendarGroupList.vue'
import CreateEquipmentModal from '../components/Settings/CreateEquipmentModal.vue'
import CreateRoomModal from '../components/Settings/CreateRoomModal.vue'
import EquipmentsListTable from '../components/Settings/EquipmentsListTable.vue'
import RoomsListTable from '../components/Settings/RoomsListTable.vue'
import ServicesListTable from '../components/Settings/ServicesListTable.vue'
import AppState from '../states/app'
import { toggleAdvancedServiceMenuView } from '../utils/commonModalFunctions'

const showNewGroupModal = ref(false)
const showNewEquipmentModal = ref(false)
const showNewRoomModal = ref(false)
const showCalendarTypeModal = ref(false)
const showCreateCalendarModal = ref(false)
const showSelectTeamModal = ref(false)
const selectedCalendarType = ref(CalendarType.EVENT)
const route = useRoute()
const groupFilter = ref('')
const calendarList = new CalendarList()
const groupList = new GroupList(
  AppState.isCalendarTypeSplitEnabled || AppState.isServicesEnabled
    ? 'sales'
    : 'all'
)

const closeGroupModal = () => {
  showNewGroupModal.value = false
}

const newGroupCreated = (groupData: any, isNew: boolean) => {
  const allCalendars = AppState.globalCalendars?.calendars || []
  getTeamsInLocation(true)
  closeGroupModal()
  AppState.selectedTab = 'groups'
  if (isNew && allCalendars?.length > 0) {
    AppState.showGroupSuccessAlert = true
    AppState.createdGroup = groupData
  }
  updateTeamsStore()
}

const openSelectTeamModal = (calendarType: CalendarType) => {
  selectedCalendarType.value = calendarType
  showCreateCalendarModal.value = true

  showCalendarTypeModal.value = false
}

const openClassBookingCalendarModal = () => {
  selectedCalendarType.value = CalendarType.CLASS_BOOKING
  showCreateCalendarModal.value = true

  showCalendarTypeModal.value = false
}

const openCollectiveBookingCalendarModal = () => {
  selectedCalendarType.value = CalendarType.COLLECTIVE
  showCreateCalendarModal.value = true

  showCalendarTypeModal.value = false
}

const openUnassignedCalendar = () => {
  selectedCalendarType.value = CalendarType.EVENT
  showCreateCalendarModal.value = true

  showCalendarTypeModal.value = false
}

const openTeamCalendar = selectedGroupId => {
  showCreateCalendarModal.value = true
}

const openCalendarTypeModal = () => {
  resetCalendarEditValidationData()
  showCalendarTypeModal.value = true
}

const newCalendarCreated = async (calendar: Calendar) => {
  showCreateCalendarModal.value = false
  AppState.showCalendarLinkModal = true
  setEditingCalendar(calendar)
  await getCalendarsInLocation(true)
  updateCalendarStore()
}

watch(
  () => AppState.showAdvancedCalendarSettings,
  () => {
    AppState.showCalendarLinkModal = false
  }
)

const openServiceMenu = () => {
  toggleAdvancedServiceMenuView()
}

onMounted(async () => {
  setIsLoading(true)
  AppState.loadingAssets.calendars = true
  await Promise.all([
    getTeamsInLocation(),
    getCalendarsInLocation(),
    getUsersInLocation(),
    getCalendarOnboardingStatus(),
  ])
  setIsLoading(false)
  AppState.loadingAssets.calendars = false
})

const openCreateResourceModal = () => {
  if (AppState.selectedTab === 'equipments') {
    showNewEquipmentModal.value = true
  } else if (AppState.selectedTab === 'rooms') {
    showNewRoomModal.value = true
  }
}

const openNewGroupModal = () => {
  showNewGroupModal.value = true
}

const closeEquipmentModal = () => {
  showNewEquipmentModal.value = false
}

const newEquipmentCreated = () => {
  getEquipmentsInLocation()
  closeEquipmentModal()
}

const closeRoomModal = () => {
  showNewRoomModal.value = false
}

const newRoomCreated = () => {
  getRoomsInLocation()
  closeRoomModal()
}

const showServiceMenuTab = computed(() => {
  return (
    !AppState.isServicesEnabled &&
    (route.query?.tab === 'services' || isServiceCalendarEnabled())
  )
})

const onAlertPrimaryClick = () => {
  AppState.showGroupSuccessAlert = false
  AppState.showSelectCalendarsModal = true
}

const onAlertSecondaryClick = () => {
  AppState.showGroupSuccessAlert = false
}

const toggleSelectCalendarsModal = () => {
  AppState.showSelectCalendarsModal = !AppState.showSelectCalendarsModal
}

const onConfirmClick = async () => {
  AppState.showSelectCalendarsModal = false
  AppState.showCalendarsMovedSuccessAlert = true
  await Promise.all([getTeamsInLocation(true), getCalendarsInLocation(true)])
  setTimeout(() => {
    AppState.showCalendarsMovedSuccessAlert = false
  }, 500)
}

const handleCloseGroupSuccessAlert = () => {
  AppState.showGroupSuccessAlert = false
}

const handleCloseCalendarsMovedSuccessAlert = () => {
  AppState.showCalendarsMovedSuccessAlert = false
}

const onGroupFilterChange = (newValue: string) => {
  groupFilter.value = newValue
}
const isCalendarEnabled = computed(() => {
  return (
    !AppState.isCalendarTypeSplitEnabled &&
    !AppState.isServicesEnabled &&
    !isRentalsEnabled.value &&
    (isServiceCalendarEnabled() || isRoomEnabled() || isEquipmentEnabled())
  )
})
</script>

<template>
  <div
    class="relative"
    :class="{
      'p-8':
        !AppState.isCalendarTypeSplitEnabled &&
        !AppState.isServicesEnabled &&
        !isRentalsEnabled,
    }"
  >
    <div
      v-if="
        AppState.showGroupSuccessAlert ||
        AppState.showCalendarsMovedSuccessAlert
      "
      class="fixed inset-0 z-[100] flex items-start justify-center"
      style="padding-top: 15vh"
    >
      <AlertPopup
        v-if="AppState.showGroupSuccessAlert"
        :show-alert="AppState.showGroupSuccessAlert"
        :title="$t('settings.calendar.group_success_message')"
        :description="$t('settings.calendar.group_success_message')"
        :on-primary-click="onAlertPrimaryClick"
        :on-secondary-click="onAlertSecondaryClick"
        :show-primary-button="true"
        :show-secondary-button="true"
        :primary-button-text="$t('settings.calendar.choose_calendars')"
        :secondary-button-text="$t('settings.calendar.do_it_later')"
        :primary-button-id="'group-success-alert-primary-button'"
        :secondary-button-id="'group-success-alert-secondary-button'"
        :handle-close="handleCloseGroupSuccessAlert"
      />

      <AlertPopup
        v-if="AppState.showCalendarsMovedSuccessAlert"
        :show-alert="AppState.showCalendarsMovedSuccessAlert"
        :title="
          $t('settings.calendar.calendars_moved_successfully', {
            name: AppState.createdGroup?.name,
          })
        "
        :description="
          $t('settings.calendar.calendars_moved_successfully', {
            name: AppState.createdGroup?.name,
          })
        "
        :max-width="'350px'"
        :handle-close="handleCloseCalendarsMovedSuccessAlert"
      />
    </div>

    <div>
      <SelectCalendarTypeModalVue_V2
        :show="showCalendarTypeModal"
        :open-unassigned-calendar-modal="openUnassignedCalendar"
        :open-team-calendar-modal="openSelectTeamModal"
        :open-class-booking-calendar-modal="openClassBookingCalendarModal"
        :open-collective-booking-calendar-modal="
          openCollectiveBookingCalendarModal
        "
        @on-modal-close="() => (showCalendarTypeModal = false)"
      />

      <CalendarLinkModal
        v-if="AppState.showCalendarLinkModal"
        :show="AppState.showCalendarLinkModal"
        @on-modal-close="() => (AppState.showCalendarLinkModal = false)"
      />
      <CreateCalendarModalWithSchedule
        v-if="showCreateCalendarModal"
        :show="showCreateCalendarModal"
        :selected-calendar-type="selectedCalendarType"
        @on-modal-close="() => (showCreateCalendarModal = false)"
        @on-confirm="newCalendarCreated"
      />
      <SelectTeamModalVue
        :show="showSelectTeamModal"
        @on-modal-close="() => (showSelectTeamModal = false)"
        @on-confirm="openTeamCalendar"
      />
      <UITabs
        v-if="isCalendarEnabled"
        v-model:value="AppState.selectedTab"
        type="line"
      >
        <UITabPane
          name="calendars"
          :tab="$t('settings.calendar.calendars')"
          class="mt-6"
        >
          <div
            v-if="AppState.isLoading"
            class="fixed inset-0 z-50 flex items-center justify-center"
          >
            <UISpin size="large" />
          </div>
          <div v-else class="flex w-full flex-row gap-7">
            <CalendarGroupList
              :group-filter="groupFilter"
              class="w-[250px]"
              :calendar-list="calendarList"
              type="sales"
              @update:group-filter="onGroupFilterChange"
              :group-list="groupList"
            />
            <CalendarListTable
              :open-calendar-type-modal="openCalendarTypeModal"
              :open-new-group-modal="openNewGroupModal"
              :group-filter="groupFilter"
              class="w-full"
              :calendar-list="calendarList"
              type="sales"
              @update:group-filter="onGroupFilterChange"
              :group-list="groupList"
            />
          </div>
        </UITabPane>
        <UITabPane
          v-if="showServiceMenuTab"
          name="services"
          :tab="$t('settings.calendar.service_menus')"
          class="mt-3"
        >
          <ServicesListTable />
        </UITabPane>

        <UITabPane
          v-if="!AppState.isServicesEnabled && isRoomEnabled()"
          name="rooms"
          :tab="$t('settings.calendar.rooms')"
          class="mt-3"
        >
          <RoomsListTable />
        </UITabPane>

        <UITabPane
          v-if="!AppState.isServicesEnabled && isEquipmentEnabled()"
          name="equipments"
          :tab="$t('settings.calendar.equipments')"
          class="mt-3"
        >
          <EquipmentsListTable />
        </UITabPane>
      </UITabs>
      <div v-else class="flex flex-row gap-4">
        <div
          v-if="AppState.isLoading"
          class="fixed inset-0 z-50 flex items-center justify-center"
        >
          <UISpin size="large" />
        </div>
        <div v-else class="mt-3 flex w-full flex-row gap-7">
          <CalendarGroupList
            :group-filter="groupFilter"
            class="w-[250px]"
            :calendar-list="calendarList"
            type="sales"
            @update:group-filter="onGroupFilterChange"
            :group-list="groupList"
          />
          <CalendarListTable
            :open-calendar-type-modal="openCalendarTypeModal"
            :open-new-group-modal="openNewGroupModal"
            :group-filter="groupFilter"
            class="w-full"
            :calendar-list="calendarList"
            type="sales"
            @update:group-filter="onGroupFilterChange"
            :group-list="groupList"
          />
        </div>
      </div>

      <CreateGroupModal
        :show="showNewGroupModal"
        @created-record="newGroupCreated"
        @close-modal="closeGroupModal"
      />
      <CreateEquipmentModal
        :show="showNewEquipmentModal"
        @created-record="newEquipmentCreated"
        @close-modal="closeEquipmentModal"
      />
      <CreateRoomModal
        :show="showNewRoomModal"
        @created-record="newRoomCreated"
        @close-modal="closeRoomModal"
      />
    </div>

    <SelectCalendars
      v-if="AppState.showSelectCalendarsModal"
      :show="AppState.showSelectCalendarsModal"
      :group="AppState.createdGroup"
      :on-cancel-click="toggleSelectCalendarsModal"
      :on-confirm-click="onConfirmClick"
    />
  </div>
</template>
